import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { QrCode, FileText, Plus, FolderOpen, Loader2, ExternalLink, CheckCircle2, FileEdit } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { FeedbackButton } from "@/components/FeedbackButton";
import api from "@/lib/api";

function StatCard({ icon: Icon, label, value }) {
  return (
    <Card className="border-slate-200 shadow-sm">
      <CardContent className="flex items-center gap-4 p-6">
        <div className="flex h-12 w-12 items-center justify-center rounded-md bg-slate-100"><Icon className="h-6 w-6 text-[#0A2540]" /></div>
        <div>
          <p className="text-2xl font-bold text-slate-900" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>{value}</p>
          <p className="text-sm text-slate-500">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [projects, setProjects] = useState(null);

  useEffect(() => {
    document.title = "Dashboard — CiteQR";
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
  }, []);

  const published = projects?.filter((p) => p.status === "published").length || 0;

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 fade-in">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-2">
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Welcome back, {user?.name?.split(" ")[0] || "there"}</h1>
          <p className="text-base text-slate-600">Manage your QR codes and academic reference pages.</p>
        </div>
        <FeedbackButton className="self-start" />
      </div>

      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard icon={FolderOpen} label="Total projects" value={projects === null ? "—" : projects.length} />
        <StatCard icon={CheckCircle2} label="Published pages" value={projects === null ? "—" : published} />
        <StatCard icon={FileEdit} label="Drafts" value={projects === null ? "—" : (projects.length - published)} />
      </div>

      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="flex items-center justify-between p-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-md bg-[#0A2540]"><QrCode className="h-6 w-6 text-[#0D9488]" /></div>
              <div><h2 className="text-base font-semibold text-slate-900">Create a QR code</h2><p className="text-sm text-slate-500">Free static QR for any link or content.</p></div>
            </div>
            <Button asChild variant="outline"><Link to="/generator" data-testid="dash-new-qr">Open</Link></Button>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="flex items-center justify-between p-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-md bg-[#0D9488]"><FileText className="h-6 w-6 text-white" /></div>
              <div><h2 className="text-base font-semibold text-slate-900">New reference page</h2><p className="text-sm text-slate-500">Publish a citation list with a QR code.</p></div>
            </div>
            <Button asChild className="bg-[#0D9488] text-white hover:bg-[#0F766E]"><Link to="/reference/new" data-testid="dash-new-reference"><Plus className="mr-1 h-4 w-4" /> Create</Link></Button>
          </CardContent>
        </Card>
      </div>

      <div className="mt-10 flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-900">Recent projects</h2>
        <Link to="/projects" className="text-sm font-medium text-[#0D9488] hover:underline">View all →</Link>
      </div>

      <div className="mt-4">
        {projects === null ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#0D9488]" /></div>
        ) : projects.length === 0 ? (
          <Card className="border-dashed border-slate-300 bg-slate-50 shadow-none">
            <CardContent className="p-10 text-center">
              <p className="text-sm text-slate-600">You haven't created any reference pages yet.</p>
              <Button asChild className="mt-4 bg-[#0D9488] text-white hover:bg-[#0F766E]"><Link to="/reference/new">Create your first page</Link></Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {projects.slice(0, 6).map((p) => (
              <Card key={p.id} className="border-slate-200 shadow-sm">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="line-clamp-2 text-sm font-semibold text-slate-900">{p.title}</h3>
                    <Badge variant={p.status === "published" ? "default" : "secondary"} className={p.status === "published" ? "bg-[#0D9488]" : ""}>{p.status}</Badge>
                  </div>
                  <p className="mt-2 text-xs text-slate-500">{p.references?.length || 0} references · {p.citation_style}</p>
                  <div className="mt-4 flex gap-2">
                    <Button asChild size="sm" variant="outline" className="flex-1"><Link to={`/reference/${p.id}/edit`}>Edit</Link></Button>
                    {p.status === "published" && (
                      <Button asChild size="sm" variant="ghost"><a href={`/r/${p.public_id}`} target="_blank" rel="noopener noreferrer" aria-label="View page"><ExternalLink className="h-4 w-4" /></a></Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
